import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BackBar from '../components/BackBar';

const API = process.env.REACT_APP_BACKEND_URL;

// Get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('opwmp_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminInvoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [presets, setPresets] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    client_name: '',
    client_company: '',
    client_address: '',
    client_abn: '',
    reference: '',
    invoice_date: new Date().toISOString().split('T')[0],
    payment_terms: 14,
    notes: '',
    include_gst: true,
    line_items: [{ description: '', quantity: 1, unit_price: 0 }]
  });

  const fetchInvoices = useCallback(async () => {
    try {
      const url = filterStatus 
        ? `${API}/api/admin/invoices?status=${filterStatus}`
        : `${API}/api/admin/invoices`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      setInvoices(response.data);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      if (err.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [filterStatus, navigate]);

  const fetchPresets = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/invoice-presets`, { headers: getAuthHeader() });
      setPresets(response.data);
    } catch (err) {
      console.error('Failed to fetch presets:', err);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchPresets();
  }, [fetchInvoices]);

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    setError('');

    // Validate line items
    const validLineItems = formData.line_items.filter(
      item => item.description && item.unit_price > 0
    );

    if (validLineItems.length === 0) {
      setError('Please add at least one line item with description and price');
      return;
    }

    try {
      await axios.post(`${API}/api/admin/invoices`, {
        ...formData,
        line_items: validLineItems
      }, { headers: getAuthHeader() });
      setShowCreateModal(false);
      resetForm();
      fetchInvoices();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleEditInvoice = async (e) => {
    e.preventDefault();
    setError('');

    // Validate line items
    const validLineItems = formData.line_items.filter(
      item => item.description && item.unit_price > 0
    );

    if (validLineItems.length === 0) {
      setError('Please add at least one line item with description and price');
      return;
    }

    try {
      await axios.put(`${API}/api/admin/invoices/${editingInvoice.id}`, {
        ...formData,
        line_items: validLineItems
      }, { headers: getAuthHeader() });
      setShowEditModal(false);
      setEditingInvoice(null);
      resetForm();
      fetchInvoices();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update invoice');
    }
  };

  const openEditModal = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      client_name: invoice.client_name,
      client_company: invoice.client_company,
      client_address: invoice.client_address,
      client_abn: invoice.client_abn || '',
      reference: invoice.reference,
      invoice_date: invoice.invoice_date,
      payment_terms: invoice.payment_terms,
      notes: invoice.notes || '',
      include_gst: invoice.include_gst !== false,
      line_items: invoice.line_items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price
      }))
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      client_company: '',
      client_address: '',
      client_abn: '',
      reference: '',
      invoice_date: new Date().toISOString().split('T')[0],
      payment_terms: 14,
      notes: '',
      include_gst: true,
      line_items: [{ description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      setFormData({
        ...formData,
        line_items: formData.line_items.filter((_, i) => i !== index)
      });
    }
  };

  const updateLineItem = (index, field, value) => {
    const updatedItems = [...formData.line_items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData({ ...formData, line_items: updatedItems });
  };

  const applyPreset = (preset, index) => {
    const updatedItems = [...formData.line_items];
    updatedItems[index] = { ...updatedItems[index], description: preset.description, unit_price: preset.unit_price };
    setFormData({ ...formData, line_items: updatedItems });
  };

  const duplicateInvoice = (invoice) => {
    setFormData({
      client_name: invoice.client_name,
      client_company: invoice.client_company,
      client_address: invoice.client_address,
      client_abn: invoice.client_abn || '',
      reference: invoice.reference,
      invoice_date: new Date().toISOString().split('T')[0],
      payment_terms: invoice.payment_terms,
      notes: invoice.notes || '',
      include_gst: invoice.include_gst !== false,
      line_items: invoice.line_items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price
      }))
    });
    setShowCreateModal(true);
  };

  const updateStatus = async (invoiceId, newStatus) => {
    try {
      await axios.put(`${API}/api/admin/invoices/${invoiceId}/status`, { status: newStatus }, { headers: getAuthHeader() });
      fetchInvoices();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const downloadPDF = async (invoice) => {
    try {
      const response = await axios.get(`${API}/api/admin/invoices/${invoice.id}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const company = (invoice.client_company || '').replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/\s+/g, '-');
      const ref = (invoice.reference || '').split(/[,;]/)[0].trim().replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/\s+/g, '-');
      const parts = [invoice.invoice_number, company, ref].filter(Boolean).join('_');
      const filename = `${parts}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
    }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/invoices/${invoiceId}`, { headers: getAuthHeader() });
      fetchInvoices();
    } catch (err) {
      console.error('Failed to delete invoice:', err);
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.line_items.reduce(
      (sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0),
      0
    );
    const gst = formData.include_gst ? subtotal * 0.1 : 0;
    const total = subtotal + gst;
    return { subtotal, gst, total };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="min-h-screen bg-gray-50">
      <BackBar to="/admin" label="Admin" />
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              data-testid="create-invoice-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Invoice
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="mb-6 flex gap-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="status-filter"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Invoice Table */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '36px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '80px' }} />
              <col style={{ width: '80px' }} />
              <col style={{ width: '90px' }} />
              <col style={{ width: '80px' }} />
              <col style={{ width: '110px' }} />
            </colgroup>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  <svg className="w-4 h-4 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    No invoices found. Create your first invoice!
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => {
                  const isPaid = invoice.status === 'paid';
                  return (
                  <tr key={invoice.id} className={`transition-colors ${isPaid ? 'bg-green-50/60' : 'hover:bg-gray-50'}`}>
                    <td className="px-2 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={isPaid}
                        onChange={() => updateStatus(invoice.id, isPaid ? 'sent' : 'paid')}
                        className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                        title={isPaid ? 'Mark as unpaid' : 'Mark as paid'}
                        data-testid={`paid-checkbox-${invoice.invoice_number}`}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className={`text-sm font-medium truncate ${isPaid ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{invoice.invoice_number}</div>
                      <div className="text-xs text-gray-500 truncate">{invoice.reference}</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className={`text-sm truncate ${isPaid ? 'text-gray-500' : 'text-gray-900'}`}>{invoice.client_company}</div>
                      <div className="text-xs text-gray-500 truncate">{invoice.client_name}</div>
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-500">
                      {formatDate(invoice.invoice_date)}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-500">
                      {formatDate(invoice.due_date)}
                    </td>
                    <td className={`px-3 py-3 text-sm font-medium ${isPaid ? 'text-gray-500' : 'text-gray-900'}`}>
                      {formatCurrency(invoice.total)}
                    </td>
                    <td className="px-3 py-3">
                      <select
                        value={invoice.status}
                        onChange={(e) => updateStatus(invoice.id, e.target.value)}
                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${getStatusColor(invoice.status)}`}
                      >
                        <option value="draft">Draft</option>
                        <option value="sent">Sent</option>
                        <option value="paid">Paid</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEditModal(invoice)} className="p-1 text-gray-500 hover:text-gray-800 rounded hover:bg-gray-100" title="Edit">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button onClick={() => downloadPDF(invoice)} className="p-1 text-blue-500 hover:text-blue-800 rounded hover:bg-blue-50" title="PDF">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        </button>
                        <button onClick={() => duplicateInvoice(invoice)} className="p-1 text-purple-500 hover:text-purple-800 rounded hover:bg-purple-50" title="Duplicate">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        </button>
                        <button onClick={() => deleteInvoice(invoice.id)} className="p-1 text-red-400 hover:text-red-700 rounded hover:bg-red-50" title="Delete">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-start justify-center min-h-screen px-4 pt-4 pb-20">
            <div 
              className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
              onClick={() => setShowCreateModal(false)}
            ></div>

            <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-auto mt-8 z-10">
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">Create Invoice</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateInvoice} className="p-6 max-h-[80vh] overflow-y-auto">
                {error && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-6 mb-6">
                  {/* Client Details */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900">Client Details</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.client_name}
                        onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="John Smith"
                        data-testid="client-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.client_company}
                        onChange={(e) => setFormData({...formData, client_company: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="ABC Developments Pty Ltd"
                        data-testid="client-company-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address *
                      </label>
                      <textarea
                        required
                        rows={3}
                        value={formData.client_address}
                        onChange={(e) => setFormData({...formData, client_address: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="123 Main Street&#10;Sydney NSW 2000"
                        data-testid="client-address-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ABN (optional)
                      </label>
                      <input
                        type="text"
                        value={formData.client_abn}
                        onChange={(e) => setFormData({...formData, client_abn: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="12 345 678 901"
                      />
                    </div>
                  </div>

                  {/* Invoice Details */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900">Invoice Details</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reference *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.reference}
                        onChange={(e) => setFormData({...formData, reference: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Project Name - Waste Management Planning Fee"
                        data-testid="reference-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Invoice Date *
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.invoice_date}
                        onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        data-testid="invoice-date-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Terms *
                      </label>
                      <select
                        value={formData.payment_terms}
                        onChange={(e) => setFormData({...formData, payment_terms: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        data-testid="payment-terms-select"
                      >
                        <option value={14}>14 Days</option>
                        <option value={21}>21 Days</option>
                        <option value={28}>28 Days</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes (optional)
                      </label>
                      <textarea
                        rows={2}
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Additional notes..."
                      />
                    </div>
                  </div>
                </div>

                {/* Line Items */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-900">Line Items</h3>
                    <button
                      type="button"
                      onClick={addLineItem}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Item
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.line_items.map((item, index) => (
                      <div key={index} className="flex gap-3 items-start bg-gray-50 p-3 rounded-lg">
                        <div className="flex-1">
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="Description"
                              data-testid={`line-item-desc-${index}`}
                            />
                            <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  const preset = presets[parseInt(e.target.value)];
                                  applyPreset(preset, index);
                                }
                              }}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                              value=""
                            >
                              <option value="">Quick Select...</option>
                              {presets.map((preset, i) => (
                                <option key={i} value={i}>
                                  {preset.description} (${preset.unit_price})
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex gap-2">
                            <div className="w-20">
                              <input
                                type="number"
                                min="0.1"
                                step="0.1"
                                value={item.quantity}
                                onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Qty"
                              />
                            </div>
                            <div className="w-32">
                              <div className="relative">
                                <span className="absolute left-3 top-2 text-gray-500">$</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.unit_price}
                                  onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                                  placeholder="Price"
                                  data-testid={`line-item-price-${index}`}
                                />
                              </div>
                            </div>
                            <div className="w-32 text-right py-2 text-sm font-medium text-gray-700">
                              {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}
                            </div>
                          </div>
                        </div>
                        {formData.line_items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="text-red-500 hover:text-red-700 p-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t pt-4 mb-6">
                  <div className="flex justify-end">
                    <div className="w-64 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">{formatCurrency(totals.subtotal)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <label className="flex items-center gap-2 cursor-pointer text-gray-600">
                          <input type="checkbox" checked={formData.include_gst} onChange={(e) => setFormData({...formData, include_gst: e.target.checked})} className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-testid="gst-toggle" />
                          GST (10%):
                        </label>
                        <span className="font-medium">{formatCurrency(totals.gst)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t pt-2">
                        <span>Total{!formData.include_gst ? ' (ex GST)' : ' (inc GST)'}:</span>
                        <span className="text-blue-600">{formatCurrency(totals.total)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    data-testid="submit-invoice-btn"
                  >
                    Create Invoice
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Invoice Modal */}
      {showEditModal && editingInvoice && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-start justify-center min-h-screen px-4 pt-4 pb-20">
            <div 
              className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
              onClick={() => { setShowEditModal(false); setEditingInvoice(null); resetForm(); }}
            ></div>

            <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-auto mt-8 z-10">
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">Edit Invoice - {editingInvoice.invoice_number}</h2>
                <button
                  onClick={() => { setShowEditModal(false); setEditingInvoice(null); resetForm(); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleEditInvoice} className="p-6 max-h-[80vh] overflow-y-auto">
                {error && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-6 mb-6">
                  {/* Client Details */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900">Client Details</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.client_name}
                        onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.client_company}
                        onChange={(e) => setFormData({...formData, client_company: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="ABC Developments Pty Ltd"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address *
                      </label>
                      <textarea
                        required
                        rows={3}
                        value={formData.client_address}
                        onChange={(e) => setFormData({...formData, client_address: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="123 Main Street&#10;Sydney NSW 2000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ABN (optional)
                      </label>
                      <input
                        type="text"
                        value={formData.client_abn}
                        onChange={(e) => setFormData({...formData, client_abn: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="12 345 678 901"
                      />
                    </div>
                  </div>

                  {/* Invoice Details */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900">Invoice Details</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reference *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.reference}
                        onChange={(e) => setFormData({...formData, reference: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Project Name - Waste Management Planning Fee"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Invoice Date *
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.invoice_date}
                        onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Terms *
                      </label>
                      <select
                        value={formData.payment_terms}
                        onChange={(e) => setFormData({...formData, payment_terms: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={14}>14 Days</option>
                        <option value={21}>21 Days</option>
                        <option value={28}>28 Days</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes (optional)
                      </label>
                      <textarea
                        rows={2}
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Additional notes..."
                      />
                    </div>
                  </div>
                </div>

                {/* Line Items */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-900">Line Items</h3>
                    <button
                      type="button"
                      onClick={addLineItem}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Item
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.line_items.map((item, index) => (
                      <div key={index} className="flex gap-3 items-start bg-gray-50 p-3 rounded-lg">
                        <div className="flex-1">
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="Description"
                            />
                            <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  const preset = presets[parseInt(e.target.value)];
                                  applyPreset(preset, index);
                                }
                              }}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                              value=""
                            >
                              <option value="">Quick Select...</option>
                              {presets.map((preset, i) => (
                                <option key={i} value={i}>
                                  {preset.description} (${preset.unit_price})
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex gap-2">
                            <div className="w-20">
                              <input
                                type="number"
                                min="0.1"
                                step="0.1"
                                value={item.quantity}
                                onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Qty"
                              />
                            </div>
                            <div className="w-32">
                              <div className="relative">
                                <span className="absolute left-3 top-2 text-gray-500">$</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.unit_price}
                                  onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                                  placeholder="Price"
                                />
                              </div>
                            </div>
                            <div className="w-32 text-right py-2 text-sm font-medium text-gray-700">
                              {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}
                            </div>
                          </div>
                        </div>
                        {formData.line_items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="text-red-500 hover:text-red-700 p-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t pt-4 mb-6">
                  <div className="flex justify-end">
                    <div className="w-64 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">{formatCurrency(totals.subtotal)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <label className="flex items-center gap-2 cursor-pointer text-gray-600">
                          <input type="checkbox" checked={formData.include_gst} onChange={(e) => setFormData({...formData, include_gst: e.target.checked})} className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-testid="gst-toggle" />
                          GST (10%):
                        </label>
                        <span className="font-medium">{formatCurrency(totals.gst)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t pt-2">
                        <span>Total{!formData.include_gst ? ' (ex GST)' : ' (inc GST)'}:</span>
                        <span className="text-blue-600">{formatCurrency(totals.total)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowEditModal(false); setEditingInvoice(null); resetForm(); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInvoices;
